import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { SellerProductReviewStatus } from "@/lib/api/types";

/** Amber star row matching the Trioty design (filled = amber, empty = track). */
export function ReviewStars({
  value,
  size = 14,
}: {
  value: number;
  size?: number;
}) {
  const rounded = Math.min(5, Math.max(0, Math.round(value)));
  return (
    <span
      className="inline-flex items-center gap-[2px]"
      aria-label={`${rounded} / 5`}
      role="img"
    >
      {[0, 1, 2, 3, 4].map((index) => (
        <svg
          key={index}
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill={index < rounded ? "#F5A623" : "var(--track)"}
          stroke="none"
          aria-hidden="true"
        >
          <path d="m12 2 2.9 6.3 6.9.6-5.2 4.6 1.6 6.8L12 17.3 5.8 20.9l1.6-6.8L2.2 8.9l6.9-.6L12 2Z" />
        </svg>
      ))}
    </span>
  );
}

/**
 * The ⋯ actions menu shared by the grid card and table row. Hide/Unhide is the
 * one wired action; it toggles the review's PUBLISHED/HIDDEN status.
 */
export function ReviewActionsMenu({
  status,
  isMutating,
  onToggleStatus,
}: {
  status: SellerProductReviewStatus;
  isMutating: boolean;
  onToggleStatus: (nextStatus: SellerProductReviewStatus) => void;
}) {
  const t = useTranslations();
  const isHidden = status === "HIDDEN";
  const nextStatus: SellerProductReviewStatus = isHidden
    ? "PUBLISHED"
    : "HIDDEN";
  return (
    <Menu as="div" className="relative inline-flex">
      <MenuButton
        className="flex h-10 w-10 items-center justify-center rounded-[9px] border border-[color:var(--card-br)] bg-[color:var(--card)] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--hover)] lg:h-8 lg:w-8"
        aria-label={t(keys.reviews.moreActions)}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
          <circle cx="12" cy="5" r="1.5" />
          <circle cx="12" cy="12" r="1.5" />
          <circle cx="12" cy="19" r="1.5" />
        </svg>
      </MenuButton>
      <MenuItems
        anchor="bottom end"
        className="z-50 mt-1 min-w-[160px] rounded-[12px] border border-[color:var(--border-light)] bg-[color:var(--card)] p-1 shadow-[0_12px_28px_rgba(0,0,0,0.16)] focus:outline-none"
      >
        <MenuItem>
          <button
            type="button"
            disabled={isMutating}
            onClick={() => onToggleStatus(nextStatus)}
            className="flex w-full items-center gap-2 rounded-[8px] px-3 py-2 text-start text-[13px] font-medium text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--hover)] disabled:opacity-50 data-[focus]:bg-[color:var(--hover)]"
          >
            {isHidden ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a13.2 13.2 0 0 1-1.67 2.68" />
                <path d="M6.06 6.06A13.16 13.16 0 0 0 2 11s3.5 7 10 7a9.12 9.12 0 0 0 5-1.44" />
                <path d="m2 2 20 20" />
              </svg>
            )}
            {isHidden ? t(keys.reviews.actionUnhide) : t(keys.reviews.actionHide)}
          </button>
        </MenuItem>
      </MenuItems>
    </Menu>
  );
}
